import React, { useEffect, useRef } from "react";
import debounce from "lodash/debounce";
import { AccountBase, InfoType, Message, MessageItem } from "../types";
import { createTopic } from "./TopicActions";
import { createMessage, sendMessage } from "./MessageActions";
import { AxiosInstance } from "axios";
import moment from "moment";
import { useModel } from "hooks/useModel";
import { DATA_TYPE_OPTIONS, JSON_DATA_DICT } from "../constants";
import { SiteInSiteSummary, SiteSummary } from "types/portfolio";
import { convertJsonToCsv, optimizeJsonData } from "../utils";
import { toast } from "react-toastify";
import {
  areSelectedListsEqual,
  getInfoTagSelectedList,
  infoTagsToMentions,
  isInfoTagLoading,
  isStringIdAccountTag,
  normalizeInfoTags,
} from "../utils/tags";

type LoadedAccountOption = {
  option: string;
  loaded: boolean;
  data?: any;
};

export const createClassicAccountTag = ({
  id = "",
  name = "",
  selectedList = [],
}: {
  id?: string;
  name?: string;
  selectedList?: string[];
} = {}): InfoType => ({
  type: "account",
  id,
  name,
  extraInfo: {
    selectedList,
  },
});

export const getClassicAccountDialogRows = (
  defaultTag: InfoType[] | undefined,
  promptData?: any
): InfoType[] => {
  if (promptData) {
    return [createClassicAccountTag({ selectedList: DATA_TYPE_OPTIONS })];
  }

  return defaultTag && defaultTag.length
    ? normalizeInfoTags(JSON.parse(JSON.stringify(defaultTag)))
    : [createClassicAccountTag()];
};

export const validateClassicAccountDialogRows = (rows: InfoType[]) =>
  rows.every((row) => !!row.name?.trim() && getInfoTagSelectedList(row).length > 0);

export const getNextClassicAccountDialogRowsBySelectedList = (
  rows: InfoType[],
  index: number,
  value: string | string[]
) => {
  let nextValue = typeof value === "string" ? value.split(",") : value;

  if (nextValue.includes("__SELECT_ALL__")) {
    const currentRow = rows[index];
    const loadedCount = currentRow ? getInfoTagSelectedList(currentRow).length : 0;

    nextValue = loadedCount === DATA_TYPE_OPTIONS.length ? [] : DATA_TYPE_OPTIONS;
  }

  const selectedList = DATA_TYPE_OPTIONS.filter((option) => nextValue.includes(option));

  return rows.map((row, rowIndex) =>
    rowIndex === index
      ? {
          ...row,
          extraInfo: {
            ...row.extraInfo,
            selectedList,
          },
        }
      : row
  );
};

const isPendingDataMessage = (message: Message | MessageItem) =>
  message.role === "system" && !!message.addedData;

const clearTrailingPendingDataMessages = <T extends Message | MessageItem>(messages: T[]): T[] => {
  let endIndex = messages.length;

  while (endIndex > 0 && isPendingDataMessage(messages[endIndex - 1])) {
    endIndex -= 1;
  }

  return messages.slice(0, endIndex);
};

// Hook - 用于管理跨组件的公共方法
export function useAppActions(axios: AxiosInstance) {
  const {
    setSelectKey,
    setChatlist,
    selectedKey,
    chatlist,
    model,
    setPrompts,
    setSearchPromptList,
    unsendMessages,
    setUnsendMessages,
    setCurrentAccountTags,
    setAccountDialogConfig,
    inputDisable,
    setInputDisable,
    isMcpService,
  } = useModel("aichat");
  const selectedKeyRef = useRef<string | null>(selectedKey ?? null);

  useEffect(() => {
    selectedKeyRef.current = selectedKey ?? null;
  }, [selectedKey]);

  const addMessage = async (topicId: string, message: Message): Promise<Message> => {
    try {
      const newMessage = await createMessage(axios, topicId, message);
      setTimeout(() => {
        axios
          .get(`/api/dataservice/v1/ai-chat/topics/${topicId}`)
          .then((_response) => {})
          .catch((error) => {
            console.error("Failed to fetch updated topic:", error);
          });
      }, 1000);
      return newMessage;
    } catch (error: any) {
      console.error("Failed to send message:", error);
      throw error;
    }
  };

  const newChatFn = async () => {
    const topic = await createTopic(axios, {
      topicName: `Chat ${moment().format("MM/DD H:mm:ss")}`,
      type: "General",
      status: 1,
    });
    setChatlist((prev) => [topic, ...prev]);
    setSelectKey(topic.id);
    return topic;
  };

  const validateSendMessage = async (
    inputValue: string,
    setLocalInput: React.Dispatch<React.SetStateAction<string>> | undefined,
    currentAccountTags: InfoType[],
    prompt?: { description: string; title: string; prompt: string },
    forcedChatId?: string | null
  ) => {
    if (inputDisable.includes(forcedChatId || selectedKeyRef.current || "")) {
      toast.warn("The input box is disabled while sending a message. Please wait.");
      return null;
    }
    if (currentAccountTags.some(isInfoTagLoading)) {
      toast.warn("Please wait for all account tags to finish loading before sending a message.");
      return null;
    }
    if (prompt?.prompt) {
      if (currentAccountTags.length === 0) {
        {
          toast.warn("Please select at least one account tag to load data before using a prompt.");
          setAccountDialogConfig({
            open: true,
            promptData: {
              inputValue,
              setLocalInput,
              prompt,
            },
          });
        }
        return null;
      }
    }
    const input = inputValue.trim();
    if (!input) return null;
    let chatId = forcedChatId || selectedKey;
    if (!chatId) {
      const res = await newChatFn();
      chatId = res?.id;
      if (!chatId) return null;
    }
    return chatId;
  };

  const sendAssistantMessage = async ({
    apiMessages,
    activeModel,
    messageTags,
    chatId,
  }: {
    apiMessages: Message[];
    activeModel: string;
    messageTags: InfoType[];
    chatId: string;
    displayContent?: Message[];
  }) => sendMessage(axios, apiMessages, activeModel, messageTags, setChatlist, chatId);

  const sendMessageFn = async (
    inputValue: string,
    setLocalInput: React.Dispatch<React.SetStateAction<string>> | undefined,
    currentAccountTags: InfoType[],
    chatId: string,
    prompt?: { description: string; title: string; prompt: string },
    prefillContent?: Message[]
  ) => {
    const input = inputValue.trim();
    const activeModel: string = model ?? "";
    try {
      setInputDisable((prev) => [...prev, chatId!]);
      const chat = chatlist.find((item) => item.id === chatId);
      const content = (prefillContent ?? (chat?.content || [])) as Message[];
      const contentForSend = isMcpService ? clearTrailingPendingDataMessages(content) : content;
      const pendingUnsendMessages = !isMcpService ? unsendMessages : [];
      const messageTags = normalizeInfoTags(currentAccountTags);
      const newMsg = {
        role: "user",
        content: prompt?.description || prompt?.title || input,
        prompt,
      };
      const baseContent = [...contentForSend, ...pendingUnsendMessages];
      const newContent = [
        ...baseContent,
        ...[newMsg],
        {
          role: "assistant",
          content: "",
          loading: true,
          accountTags: messageTags,
        },
      ];
      setLocalInput?.("");
      setChatlist((prev) =>
        prev.map((item) => {
          if (item.id === chatId) {
            return {
              ...item,
              content: newContent,
              accountTags: messageTags,
              mentions: infoTagsToMentions(messageTags),
            };
          }
          return item;
        })
      );
      pendingUnsendMessages.forEach(async (msg) => {
        await addMessage(chatId!, msg);
      });
      setUnsendMessages([]);
      await addMessage(chatId, newMsg);
      const [errorMsg, replyMessage] = await sendAssistantMessage({
        apiMessages: newContent as Message[],
        activeModel,
        messageTags,
        chatId,
      });
      if (errorMsg) {
      }
      if (replyMessage) {
      }
      await addMessage(
        chatId,
        replyMessage || {
          role: "assistant",
          content: `Error: ${errorMsg}`,
          accountTags: messageTags,
        }
      );

      if (!selectedKey) {
        setTimeout(() => {
          axios.get(`/api/dataservice/v1/ai-chat/topics/${chatId}`).then((res) => {
            setChatlist((prev) =>
              prev.map((i) => {
                if (i.id === chatId) {
                  return {
                    ...i,
                    topicName: res.data.topicName,
                  };
                }
                return i;
              })
            );
          });
        }, 3000);
      }
    } finally {
      setInputDisable((prev) => prev.filter((id) => id !== chatId));
    }
  };

  const updatePromptFn = (id: string, data: any, type?: string) => {
    if (type === "delete") {
      setPrompts((prev) =>
        prev.map((item) => {
          return {
            ...item,
            list: item.list.filter((prompt) => prompt.id !== id),
          };
        })
      );
      setSearchPromptList((prev) => prev.filter((prompt) => prompt.id !== id));
    } else if (type === "add") {
      setPrompts((prev) =>
        prev.map((item) => {
          const newItem = {
            ...item,
            list: [
              {
                ...data,
                id,
              },
              ...item.list,
            ],
          };
          if (
            item.type === "All" ||
            (item.type === "system" && data.promptType === "system") ||
            (item.type === "public" && data.promptVisibility === "public") ||
            (item.type === "private" && data.promptVisibility === "private") ||
            (item.type === "favorite" && data.favorite)
          ) {
            return newItem;
          } else {
            return item;
          }
        })
      );
    } else {
      setPrompts((prev) =>
        prev.map((item) => {
          const hasId = item.list.some((i) => i.id === id);
          const newItem = {
            ...item,
            list: hasId
              ? item.list.map((i) => (i.id === id ? { ...i, ...data } : i))
              : [...item.list, { id, ...data }],
          };
          if (
            item.type === "All" ||
            (item.type === "system" && data.promptType === "system") ||
            (item.type === "public" && data.promptVisibility === "public") ||
            (item.type === "private" && data.promptVisibility === "private") ||
            (item.type === "favorite" && data.favorite)
          ) {
            return newItem;
          } else {
            return {
              ...item,
              list: item.list.filter((prompt) => prompt.id !== id),
            };
          }
        })
      );
      setSearchPromptList((prev) => {
        const hasId = prev.some((i) => i.id === id);
        return hasId
          ? prev.map((i) => (i.id === id ? { ...i, ...data } : i))
          : [...prev, { id, ...data }];
      });
    }
  };

  // getLoadedMessage
  const getAccountData = async (
    account: AccountBase,
    option: string
  ): Promise<{ account: AccountBase; option: string; data: any }> => {
    // Simulate an API call to get account data
    let accountKey = account.accountKey || "";
    switch (option) {
      case "Account Details":
        const res = await axios.post(
          "/api/portfolio/accounts",
          {
            sorts: [
              {
                field: "priorityIndicatorRank",
                sort: "asc",
              },
            ],
            filters: [
              {
                field: "accountId",
                operator: "eq",
                value: account.accountId,
              },
            ],
            searchEnable: false,
            keyword: "",
            viewType: "my",
          },
          {
            headers: {
              "Page-Number": 0,
              "Page-Size": 1,
            },
          }
        );
        return {
          account,
          option,
          data: { accountDetailData: res.data },
        };

      case "Subscription Details":
        const { data } = await axios.get(
          `/api/dataservice/v1/accounts/${account.accountId}/subscriptions?includeSite=true&includeSku=true&includeSiteInactive=true&includeRenewal=true&includeHealthInfo=true&latestSubscriptionOnly=1&sorts=invoiceArr:desc&usingBundle=true`
        );
        const optimizedJsonData = optimizeJsonData(data);
        // console.log("Optimized Data:", optimizedJsonData);
        return {
          account,
          option,
          data: { subscriptionSkuSiteData: optimizedJsonData ?? data },
        };

      case "Success Plan Details":
        const { data: successPlanData } = await axios.get(
          `/api/dataservice/v1/success-plans?accountId=${account.accountId}`
        );
        return {
          account,
          option,
          data: {
            successPlanData: {
              createdDate: successPlanData.createdDate,
              createdBy: successPlanData.createdBy,
              lastModifiedDate: successPlanData.lastModifiedDate,
              lastModifiedBy: successPlanData.lastModifiedBy,
              customerRisk: successPlanData.customerRisk,
              customerRiskNote: successPlanData.customerRiskNote,
              meetingRisk: successPlanData.meetingRisk,
              meetingRiskNote: successPlanData.meetingRiskNote,
              callingRisk: successPlanData.callingRisk,
              callingRiskNote: successPlanData.callingRiskNote,
              contactCenterRisk: successPlanData.contactCenterRisk,
              contactCenterRiskNote: successPlanData.contactCenterRiskNote,
              deviceRisk: successPlanData.deviceRisk,
              deviceRiskNote: successPlanData.deviceRiskNote,
              ciscoExeSponsor: successPlanData.ciscoExeSponsor,
              collabExeSponsor: successPlanData.collabExeSponsor,
            },
          },
        };

      case "Recent Activities":
        const { data: activitiesData } = await axios.post(
          "/api/dataservice/v1/activities/smart-search",
          {
            includeTasks: 1,
            includePlaycards: 1,
            includeComments: 1,
            filters: [
              {
                field: "accountId",
                operator: "eq",
                value: account.accountId,
              },
            ],
            sorts: [
              {
                field: "createdDate",
                order: "desc",
              },
            ],
            viewType: "all",
          },
          {
            headers: {
              "Page-Number": 0,
              "Page-Size": 3,
            },
          }
        );

        const optimizedRawData = activitiesData.map((item: any) => ({
          type: item.type,
          activityName: item.activityName,
          activityStatus: item.activityStatus,
          createdDate: item.createdDate,
          assigneeName: item.assigneeName,
          lastModifiedDate: item.lastModifiedDate,
          overdue: item.overdue,
          closedDate: item.closedDate,
          allTasks: item.allTasks,
          closedTasks: item.closedTasks,
          overdueTasks: item.overdueTasks,
          products: item.products,
          purpose: item.purpose,
          description: item.description,
          tasks:
            Array.isArray(item.tasks) && item.tasks.length > 0
              ? item.tasks.map((task: any) => ({
                  taskName: task.taskName,
                  taskStatus: task.taskStatus,
                  taskDueDate: task.dueDate,
                  taskClosedDate: task.closedDate,
                  taskDescription: task.taskDescription,
                  taskAssigneeRole: task.taskAssigneeRole,
                  taskAssigneeName: task.taskAssigneeName,
                }))
              : [],
          comments:
            Array.isArray(item.comments) && item.comments.length > 0
              ? item.comments.map((comment: any) => ({
                  comment: comment.text,
                  createdBy: comment.createdBy,
                  createdDate: comment.createdDate,
                  creatorName: comment.creatorName,
                }))
              : [],
        }));

        return {
          account,
          option,
          data: {
            activitiesData: optimizeJsonData(optimizedRawData) ?? optimizedRawData,
          },
        };

      case "Webex Meeting Usage":
        const { data: siteSummaryArr } = await axios.get<SiteSummary[]>(
          `/api/dataservice/v1/accounts/${account.accountId}/site-summary`
        );

        // If siteSummaryArr is an array, get the first element or handle accordingly
        const siteSummary = Array.isArray(siteSummaryArr) ? siteSummaryArr[0] : siteSummaryArr;

        if (!siteSummary || !siteSummary.sites || siteSummary.sites.length === 0) {
          return {
            account,
            option,
            data: {
              webexMeetingUsage: "No webex meeting usage",
              webexMeetingUtilization: "No webex meeting utilization",
            },
          };
        }

        const { data: CHAligedUsageData } = await axios.get(
          `/api/summary/usage/meeting?hgsSiteId=${siteSummary?.sites
            ?.filter((x: SiteInSiteSummary) => x.hasUsage)
            .map((x: SiteInSiteSummary) => x.hgsSiteId)
            .join(",")}&rangeType=monthly&rangeValue=13`
        );

        const { data: utilizationData } = await axios.get(
          `/api/summary/usage/meeting/legacy/utilization?siteNames=${siteSummary?.sites
            ?.filter((x: SiteInSiteSummary) => x.hasUsage)
            .map((x: SiteInSiteSummary) => x.siteUrl.slice(0, -10))
            .join(",")}&rangeValue=12&deploymentModel=${
            siteSummary?.sites?.filter((x: SiteInSiteSummary) => x.hasUsage)[0].deploymentModel
          }`
        );

        return {
          account,
          option,
          data: {
            webexMeetingUsage: {
              categories: CHAligedUsageData.categories,
              data: {
                activeHosts: CHAligedUsageData.data.activeHosts,
                totalMeetings: CHAligedUsageData.data.totalMeetings,
                totalVideoMeetingsVideoMeetings:
                  CHAligedUsageData.data.totalVideoMeetingsVideoMeetings,
                totalMeetingsMinutesMeetingMinutes:
                  CHAligedUsageData.data.totalMeetingsMinutesMeetingMinutes,
                totalPeopleMinutes: CHAligedUsageData.data.totalPeopleMinutes,
                sharingMeetings: CHAligedUsageData.data.sharingMeetings,
              },
            },
            webexMeetingUtilization: utilizationData,
          },
        };

      case "Webex Calling Usage":
        if (!accountKey || accountKey === "") {
          const { data: accountData } = await axios.get(
            `/api/dataservice/v1/accounts/${account.accountId}?includeSite=false&usingBundle=false&includeSubscriptions=false`
          );
          accountKey = accountData.accountKey;
        }

        const { data: ciOrgsData } = await axios.get(
          `/api/dataservice/v1/accounts/${accountKey}/ci-organizations`
        );
        if (!ciOrgsData || ciOrgsData.length === 0) {
          return {
            account,
            option,
            data: { callingUsageData: "No calling usage data" },
          };
        }

        const { data: callingUsageData } = await axios.post(
          `/api/dataservice/v1/ci-organizations/webex-calling-usage`,
          {
            rangeType: "monthly",
            dateRange: 13,
            orgIds: ciOrgsData.map((org: any) => org.ciOrgId),
          }
        );
        return {
          account,
          option,
          data: { callingUsageData },
        };

      case "Webex Messaging Usage":
        const { data: messagingActiveUserData } = await axios.get(
          `/api/summary/usage/teams/active-users?accountId=${account.accountId}&rangeType=monthly&dateRange=12`
        );
        const { data: messagingUsageData } = await axios.get(
          `/api/summary/usage/teams/teams-usage?accountId=${account.accountId}&dateRange=12`
        );
        return {
          account,
          option,
          data: { messagingActiveUserData, messagingUsageData },
        };

      // Simulated data for other options
      case "Technical Support Cases":
        const { data: TechnicalSupportCasesData } = await axios.get(
          `/api/dataservice/v1/accounts/${account.accountId}/case-summary?months=12`
        );
        if (!accountKey || accountKey === "") {
          const { data: accountData } = await axios.get(
            `/api/dataservice/v1/accounts/${account.accountId}?includeSite=false&usingBundle=false&includeSubscriptions=false`
          );
          accountKey = accountData.accountKey;
        }
        const { data: TechnicalSupportCasesDetailData } = await axios.get(
          `/api/dataservice/v1/accounts/${accountKey}/cases-list?caseType=TAC,BEMS,CAP&status=Open&sorts=severity:asc,date:desc`
        );
        return {
          account,
          option,
          data: {
            TechnicalSupportCasesData,
            TechnicalSupportCasesDetailData: convertJsonToCsv(
              TechnicalSupportCasesDetailData,
              true
            ),
          },
        };
      case "My Notes":
        const { data: myNotesData } = await axios.get(`/api/dataservice/v1/my-notes`, {
          params: {
            accountId: account.accountId,
            pageSize: 100,
            pageNumber: 0,
            archiveFolder: false,
            sortBy: "createdDate:desc",
          },
        });
        return {
          account,
          option,
          data: { myNotesData: convertJsonToCsv(myNotesData, true) },
        };

      default:
        return {
          account,
          option,
          data: `No data available for option: ${option}`,
        };
    }
  };
  const loadAccountData = async (
    accountTag: InfoType
  ): Promise<{ account: AccountBase; options: LoadedAccountOption[] }> => {
    const selectedList = getInfoTagSelectedList(accountTag);
    const account: AccountBase = {
      accountId: typeof accountTag.id === "string" ? accountTag.id : "",
      accountName: accountTag.name,
    };

    if (!selectedList.length || !account.accountId) {
      return { account, options: [] };
    }

    const loadPromises = selectedList.map((option) => getAccountData(account, option));
    const results = await Promise.allSettled(loadPromises);

    return {
      account,
      options: selectedList.map((option) => {
        const result = results.find(
          (res) => res.status === "fulfilled" && res.value.option === option
        ) as
          | PromiseFulfilledResult<{
              account: AccountBase;
              option: string;
              data: any;
            }>
          | undefined;

        return {
          option,
          loaded: true,
          ...(result ? { data: result.value.data } : {}),
        };
      }),
    };
  };
  const getLoadedMessage = async (
    rows: InfoType[],
    defaultTag: InfoType[],
    chatId: string | null
  ): Promise<{ content: MessageItem[] | null; chatId: string | null }> => {
    const normalizedRows = normalizeInfoTags(rows);
    const normalizedDefaultTag = normalizeInfoTags(defaultTag);
    const loadableRows = normalizedRows.filter(isStringIdAccountTag);
    const changedRows: InfoType[] = [];
    const loadingMessageId = `loading-${Date.now()}`;
    const rowAccountIds = new Set(loadableRows.map((row) => row.id));

    loadableRows.forEach((row) => {
      const defaultRow = normalizedDefaultTag.find(
        (tag) => tag.type === "account" && tag.id === row.id
      );

      if (!defaultRow) {
        changedRows.push(row);
        return;
      }

      const hasChanges = !areSelectedListsEqual(
        getInfoTagSelectedList(row),
        getInfoTagSelectedList(defaultRow)
      );

      if (hasChanges) {
        changedRows.push(row);
      }
    });

    const changedRowAccountIds = new Set(
      changedRows.filter(isStringIdAccountTag).map((row) => row.id)
    );

    if (!chatId) {
      setUnsendMessages((prev) =>
        prev.filter(
          (msg) =>
            !!msg.loadedAccountId &&
            rowAccountIds.has(msg.loadedAccountId) &&
            !changedRowAccountIds.has(msg.loadedAccountId)
        )
      );
    }

    const loadingRows = normalizedRows.map((tag) =>
      changedRows.some(
        (row) => row.type === "account" && tag.type === "account" && row.id === tag.id
      )
        ? { ...tag, state: "loading" as const }
        : tag
    );

    if (changedRows.length > 0 && chatId) {
      const targetId = chatId;
      setChatlist((prev) =>
        prev.map((item) => {
          if (item.id === targetId) {
            const existingContent = item?.content || [];
            const nextContent = [
              ...existingContent,
              {
                id: loadingMessageId,
                role: "assistant",
                content: "",
                loading: true,
                accountTags: loadingRows,
              },
            ];
            return {
              ...item,
              content: nextContent,
              accountTags: loadingRows,
              mentions: infoTagsToMentions(loadingRows),
            };
          }
          return item;
        })
      );
    }

    setCurrentAccountTags(loadingRows);

    if (changedRows.length > 0) {
      const loadedMessages = changedRows.map(async (row) => {
        const selectedList = getInfoTagSelectedList(row);
        const loadedAccountTag = await loadAccountData(row);
        const messageModel: string | undefined = model ?? undefined;
        const successPlanOption2 = loadedAccountTag.options.find(
          (opt) => opt.option === "Success Plan Details"
        );
        const successPlanLastModified2 =
          successPlanOption2?.data?.successPlanData?.lastModifiedDate;
        const successPlanInstructions2 = successPlanLastModified2
          ? `\n\nIMPORTANT: When using Success Plan data in your response, you MUST explicitly mention that this data was last updated on ${moment(successPlanLastModified2).format("MMMM DD, YYYY")} (${moment(successPlanLastModified2).fromNow()}). This helps users understand if the data might be outdated.`
          : "";
        const systemMessage: MessageItem = {
          id: Date.now().toString(),
          role: "system",
          loadedAccountId: typeof row.id === "string" ? row.id : undefined,
          content: `**Loaded info** \n **Account**: ${row.name} \n **Data**: ${selectedList.join(";")}`,
          addedData:
            `I will provide multiple JSON data for the account ${row.name}, Please answer the following questions based on these data sets.${successPlanInstructions2} \n\n` +
            `** JSON Data for the JSON field mapping dictionary:** fieldMappingDictionary (fieldMappingDictionary)\n ` +
            "```json\n" +
            JSON.stringify(JSON_DATA_DICT) +
            "``` \n\n" +
            loadedAccountTag.options
              .map(
                (opt) =>
                  `** JSON Data for The Account ${row.name}: ${opt.option} (${opt.option})** \n ` +
                  "```json\n" +
                  JSON.stringify(opt.data) +
                  "```"
              )
              .join("\n\n"),
          time: new Date().toISOString(),
          accountTags: normalizedRows,
          model: messageModel,
        };
        return systemMessage;
      });
      const selectedKeySnapshot = selectedKeyRef.current;
      const resolvedMessages = await Promise.all(loadedMessages);
      const finalRows = normalizedRows.map((tag) =>
        changedRows.some(
          (row) => row.type === "account" && tag.type === "account" && row.id === tag.id
        )
          ? { ...tag, state: "completed" as const }
          : tag
      );
      const isSelectedKeyUnchanged = selectedKeySnapshot === selectedKeyRef.current;
      if (isSelectedKeyUnchanged) {
        setCurrentAccountTags(finalRows);
      }
      if (chatId) {
        const targetId = chatId;
        const currentChat = chatlist.find((item) => item.id === targetId);
        const nextContent = [
          ...(currentChat?.content || []).filter((msg: any) => msg?.id !== loadingMessageId),
          ...resolvedMessages,
        ] as MessageItem[];
        setChatlist((prev) =>
          prev.map((item) => {
            if (item.id === targetId) {
              return {
                ...item,
                content: nextContent,
                accountTags: finalRows,
                mentions: infoTagsToMentions(finalRows),
              };
            }
            return item;
          })
        );
        resolvedMessages.forEach(async (msg: MessageItem) => {
          if (targetId) {
            await addMessage(targetId, msg);
          }
        });
        return { content: nextContent, chatId: targetId || null };
      } else {
        if (isSelectedKeyUnchanged) {
          setUnsendMessages((prev) =>
            [...prev, ...resolvedMessages].filter(
              (msg) => !!msg.loadedAccountId && rowAccountIds.has(msg.loadedAccountId)
            )
          );
        }
        return { content: resolvedMessages, chatId: null };
      }
    }
    return { content: null, chatId };
  };

  const debouncedSendMessageFn = debounce(
    async (
      inputValue: string,
      setLocalInput: React.Dispatch<React.SetStateAction<string>> | undefined,
      currentAccountTags: InfoType[],
      prompt?: { description: string; title: string; prompt: string },
      forcedChatId?: string | null,
      prefillContent?: Message[]
    ) => {
      const chatId = await validateSendMessage(
        inputValue,
        setLocalInput,
        currentAccountTags,
        prompt,
        forcedChatId
      );
      if (!chatId) return;
      sendMessageFn(inputValue, setLocalInput, currentAccountTags, chatId, prompt, prefillContent);
    },
    300
  );
  return {
    addMessage,
    newChatFn,
    sendMessageFn: debouncedSendMessageFn,
    basicSendMessageFn: sendMessageFn,
    updatePromptFn,
    validateSendMessage,
    getLoadedMessage,
  };
}
